// src/components/email/ReplyModal.tsx
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { UserMessage } from '../userMessages';
import {
  Send,
  Users,
  Plus,
  Mail,
} from 'lucide-react';

type Recipient = {
  email: string;
  name?: string;
};

type ReplyModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  replyBody: string;
  setReplyBody: (val: string) => void;
  availableContacts: Recipient[];
  onSubmit: (threadId: string, replyBody: string, toRecipients: string[]) => Promise<void>;
  thread: UserMessage ;
  addresses: any[];
  recipients: string[]|undefined;
};

// Component for managing recipient input with search functionality
const RecipientInput: React.FC<{
  staticRecipients: any[];
  recipients: string[];
  onRecipientsChange: (recipients: string[]) => void;
  availableContacts: Recipient[];
  placeholder: string;
}> = ({ staticRecipients, recipients, onRecipientsChange, availableContacts, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [filteredContacts, setFilteredContacts] = useState<Recipient[]>([]);

  const addRecipient = (email: string) => {
    if (email && !recipients.includes(email)) {
      onRecipientsChange([...recipients, email]);
    }
    setInputValue('');
  };

  const removeRecipient = (emailToRemove: string) => {
    onRecipientsChange(recipients.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const email = inputValue.trim();
      if (email) {
        addRecipient(email);
      }
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={e => {
          const val = e.target.value;
          setInputValue(val);
          setFilteredContacts(
            availableContacts.filter(c =>
              c.email.toLowerCase().includes(val.toLowerCase()) || c.name?.toLowerCase().includes(val.toLowerCase())
            ).slice(0, 5)
          );
        }}
        onKeyDown={handleKeyPress}
        className={`text-sm bg-gray-50 border-0 focus:bg-white focus:ring-1 focus:ring-blue-500 ${
          !isValidEmail(inputValue) && inputValue ? 'ring-1 ring-red-300' : ''
        }`}
        placeholder={placeholder}
      />
      {inputValue && filteredContacts.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-auto">
          {filteredContacts.map(contact => (
            <button
              key={contact.email}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
              onClick={() => addRecipient(contact.email)}
            >
              <div className="font-medium text-gray-900">{contact.name || contact.email}</div>
              {contact.name && (
                <div className="text-gray-500 text-xs">{contact.email}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReplyModal: React.FC<ReplyModalProps> = ({
  isOpen,
  onRequestClose,
  replyBody,
  setReplyBody,
  availableContacts,
  onSubmit,
  thread,
  addresses,
  recipients,
}) => {
  const [userRecipients, setUserRecipients] = useState<Recipient[]>([]);

console.log("thread in reply modal", recipients);


  if (!thread.id) {
    console.warn('ReplyModal: no thread provided');
    return null;
  }


  return (
    <Dialog open={isOpen} onOpenChange={onRequestClose}>
      <DialogContent className="sm:max-w-[80vw] w-full h-[85vh] p-0 flex flex-col gap-0 overflow-hidden bg-white sm:rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-gray-600" />
            <DialogTitle className="text-lg font-semibold text-gray-900">Reply to: {thread.subject}</DialogTitle>
          </div>
        </div>

                  {/* Content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Recipients Section */}
                  <div className="px-6 py-4 border-b border-gray-200 bg-white">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="h-4 w-4 text-gray-600" />
                      <label className="text-sm font-semibold text-gray-700">Recipients</label>
                    </div>
                    
                    <div className="flex gap-6">
                                    {/* Left Column: Add Recipients */}
                                    <div className="w-1/3 max-w-[40rem] pt-1">
                                      <div className="flex flex-col gap-2">
                                        <span className="text-xs font-medium text-gray-500">Add Recipients:</span>
                                        <div className="relative">                            <RecipientInput
                              staticRecipients={[]}
                              recipients={userRecipients.map(r => r.email)}
                              onRecipientsChange={emails =>
                                setUserRecipients(emails.map(email => ({ email } as Recipient)))
                              }
                              availableContacts={addresses as Recipient[]}
                              placeholder="Type email or search..."
                            />
                          </div>
                        </div>
                      </div>
        
                      {/* Right Column: Recipient Lists */}
                      <div className="flex-1 space-y-3 border-l pl-6 border-gray-100">
                        {/* Original Recipients */}
                        {recipients && recipients.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-medium text-gray-500 w-8 mt-1.5">To:</span>
                            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto flex-1">
                              {recipients.map((email, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded border"
                                >
                                  {email}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Additional Recipients */}
                        {userRecipients.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-medium text-gray-500 w-8 mt-1.5">CC:</span>
                            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto flex-1">
                              {userRecipients.map((recipient, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded border border-blue-200"
                                >
                                  {recipient.email}
                                  <button
                                    onClick={() => {
                                      const emails = userRecipients.filter((_, i) => i !== index).map(r => r.email);
                                      setUserRecipients(emails.map(email => ({ email } as Recipient)));
                                    }}
                                    className="ml-1 text-blue-600 hover:text-blue-800 font-bold"
                                    type="button"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
          {/* Message Editor */}
          <div className="flex-1 flex flex-col px-6 py-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="h-4 w-4 text-gray-600" />
              <label className="text-sm font-semibold text-gray-700">Message</label>
            </div>
            <div className="flex-1 border border-gray-300 rounded-lg overflow-hidden">
              <ReactQuill
                theme="snow"
                value={replyBody}
                onChange={setReplyBody}
                style={{ height: 'calc(100% - 42px)' }}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['link'],
                    ['clean']
                  ]
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>Recipients: {(recipients?.length || 0) + userRecipients.length}</span>
            <span>Characters: {replyBody.replace(/<[^>]*>/g, '').length}</span>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                onSubmit(thread.id, replyBody, userRecipients.map((r: Recipient) => r.email));
                onRequestClose();
              }}
              disabled={replyBody.trim().length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Reply
            </Button>
            <Button 
              variant="ghost" 
              onClick={onRequestClose}
              className="px-4 py-2"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReplyModal;
